(function (root) {
  'use strict';
  const profiles = typeof module !== 'undefined' && module.exports ? require('./knife-finder-v2-profiles.js') : root.DalstrongKnifeFinderV2Profiles;
  const tasks = {
    everyday: { chef: 45, santoku: 43 },
    vegetables: { nakiri: 48, santoku: 44, chef: 35 },
    bbq: { slicer: 48 },
    butchery: { cleaver: 48, butcher: 45 },
    trimming: { boning: 48 },
    fish: { fillet: 48 },
    bread: { bread: 48 },
    detail: { paring: 48, utility: 40 }
  };
  // Related shapes fill a shortlist only after the purpose-built options.
  const related = {
    everyday: { utility: 24, nakiri: 20 },
    vegetables: { utility: 24, paring: 20 },
    bbq: { chef: 24 },
    butchery: { boning: 28, chef: 24, slicer: 18 },
    trimming: { fillet: 28, utility: 22, paring: 18 },
    fish: { boning: 28, utility: 22, chef: 18 },
    bread: { slicer: 24, chef: 20 },
    detail: { boning: 24, chef: 18, santoku: 18 }
  };
  const size = p => !Number.isFinite(p.length) || p.length <= 0 ? null : p.length <= 7 ? 'compact' : p.length <= 8.5 ? 'classic' : 'long';
  const visible = (id, answers) => id === 'owned' ? answers.intent === 'expand' : id === 'upgrade' ? answers.intent === 'upgrade' : id === 'meat' ? answers.task === 'butchery' : id === 'format' ? ['first','gift'].includes(answers.intent) : true;
  const priorityOptions = answers => ['budget', 'steel', 'size', 'style'].filter(key => key === 'budget' ? ['100', '150', '250'].includes(answers.budget) : Boolean(answers[key] && answers[key] !== 'any')).concat('any');
  const steps = (questions, answers) => questions.filter(q => visible(q.id, answers) && (q.id !== 'priority' || priorityOptions(answers).length > 1)).map(q => q.id === 'priority' ? { ...q, options: q.options.filter(value => priorityOptions(answers).includes(value)) } : q);
  function sanitize(questions, answers) {
    const clean = {};
    for (const q of questions) {
      const value = answers?.[q.id];
      if (q.multi && Array.isArray(value)) {
        const valid = [...new Set(value.filter(v => q.options.includes(v)))];
        if (valid.length) clean[q.id] = valid.includes('none') ? ['none'] : valid;
      } else if (q.options.includes(value)) clean[q.id] = value;
    }
    for (const q of questions) if (!visible(q.id, clean)) delete clean[q.id];
    if (!priorityOptions(clean).includes(clean.priority) || priorityOptions(clean).length === 1) delete clean.priority;
    return clean;
  }
  function rank(products, answers) {
    const weights = answers.task === 'butchery' && answers.meat === 'boneless' ? { butcher: 52, slicer: 46, chef: 32, boning: 30 } : tasks[answers.task];
    if (!weights) return [];
    const cap = ['100', '150', '250'].includes(answers.budget) ? Number(answers.budget) * 100 : Infinity;
    const owned = answers.intent === 'expand' && Array.isArray(answers.owned) ? answers.owned : [];
    const seen = new Set();
    const priority = priorityOptions(answers).includes(answers.priority) ? answers.priority : 'any';
    const candidates = products.map(profiles.prepare).filter(p => {
      if (!p.id || !p.available || !p.variant || !Number.isFinite(p.price) || p.price <= 0 || seen.has(String(p.id))) return false;
      if (!['chef', 'santoku', 'nakiri', 'slicer', 'boning', 'fillet', 'bread', 'paring', 'utility', 'cleaver', 'butcher', 'steak', 'specialty'].includes(p.kind) || !['easy', 'fine', 'unknown'].includes(p.care)) return false;
      if ((!size(p) && p.length !== null) || !['german', 'japanese', 'other', 'unknown'].includes(p.steel) || !['classic', 'dark', 'natural', 'bold', 'unknown'].includes(p.style)) return false;
      seen.add(String(p.id)); return true;
    }).map(p => {
      const matchingKind = p.isSet ? [...(p.members || [])].sort((a,b) => (weights[b] || 0) - (weights[a] || 0))[0] : p.kind;
      const boneWork = answers.task === 'butchery' && ['bones', 'both'].includes(answers.meat);
      const taskTier = boneWork ? p.boneCapable && !p.isSet ? 3 : 0 : weights[matchingKind] ? 2 : related[answers.task][matchingKind] ? 1 : 0;
      let score = (weights[matchingKind] || related[answers.task][matchingKind] || 0) - (p.isSet ? 12 : 0);
      const reasons = taskTier >= 2 ? [boneWork ? 'bone_work' : p.isSet ? 'set_task' : answers.task === 'butchery' && answers.meat === 'boneless' ? 'boneless_work' : 'task'] : [];
      const tradeoffs = taskTier >= 2 ? [] : [boneWork ? 'bone_work' : 'task'];
      for (const key of ['size', 'steel', 'style']) {
        if (answers[key] && answers[key] !== 'any') {
          if ((key === 'size' ? size(p) === answers[key] : key === 'style' ? (p.styles || [p.style]).includes(answers[key]) : p[key] === answers[key])) { score += 30; reasons.push(key); }
          else tradeoffs.push((key === 'size' ? !size(p) : p[key] === 'unknown') ? key + '_unknown' : key);
        }
      }
      if (answers.care === p.care) { score += 16; reasons.push('care'); }
      else if (answers.care === 'easy') tradeoffs.push(p.care === 'unknown' ? 'care_unknown' : 'care');
      if (answers.experience === 'learning' && p.care === 'easy' && !['cleaver','butcher','specialty'].includes(p.kind) && !p.isSet) { score += 9; reasons.push('learning'); }
      if (['enthusiast', 'pro'].includes(answers.experience) && p.care === 'fine') score += 5;
      if (Number.isFinite(cap)) {
        if (p.price <= cap) { score += 25; reasons.push('budget'); }
        else { score -= Math.min(priority === 'budget' ? 180 : 30, (p.price - cap) / cap * (priority === 'budget' ? 180 : 30)); tradeoffs.push('budget'); }
      }
      if (owned.length && !owned.includes('none') && !p.isSet) {
        if (owned.includes(p.kind)) tradeoffs.push('owned');
        else { score += 20; reasons.push('gap'); }
      }
      if (answers.handling && answers.handling !== 'any') {
        if (p.handling === answers.handling) { score += 32; reasons.push('handling_fit'); }
        else tradeoffs.push(p.handling === 'unknown' ? 'handling_unknown' : 'handling_fit');
      }
      if (p.handling === 'heavy') tradeoffs.push('handling');
      if (answers.intent === 'upgrade') {
        const improvement = answers.upgrade === 'reach' ? size(p) === 'long' : answers.upgrade === 'upkeep' ? p.care === 'easy' : answers.upgrade === 'comfort' ? p.handling === 'light' || p.handling === 'balanced' : answers.upgrade === 'style' ? (p.styles || []).includes('bold') : false;
        if (improvement) { score += 32; reasons.push('upgrade_' + answers.upgrade); }
      }
      if (answers.format && answers.format !== 'any') {
        if (p.isSet === (answers.format === 'set')) { score += 100; reasons.push('format'); }
        else tradeoffs.push('format');
      }
      if (p.isSet) tradeoffs.push('set');
      if (!reasons.length) reasons.push('available');
      const priorityFit = priority !== 'any' && reasons.includes(priority) ? 1 : 0;
      const statementFit = answers.style === 'bold' && reasons.includes('style') ? p.statementStrength : 0;
      return { ...p, taskTier, priorityFit, statementFit, score, reasons, tradeoffs };
    });
    const shortlist = [];
    const covered = new Set();
    const series = new Set();
    // Let the alternatives cover preferences the first choice could not meet.
    // Task suitability still takes priority, and no bonus is used for the top pick.
    const coverage = p => shortlist.length ? p.reasons.filter(key => ['size', 'steel', 'style'].includes(key) && !covered.has(key)).length * 80 : 0;
    // Prefer different series among similarly suitable choices; never cross purpose,
    // explicit priority or statement-style tiers merely to add variety.
    const variety = p => series.has(p.series) ? -35 : 0;
    while (shortlist.length < 3 && candidates.length) {
      candidates.sort((a, b) => b.taskTier - a.taskTier || b.priorityFit - a.priorityFit || b.statementFit - a.statementFit || (b.score + coverage(b) + variety(b)) - (a.score + coverage(a) + variety(a)) || a.price - b.price || String(a.id).localeCompare(String(b.id)));
      const next = candidates.shift();
      shortlist.push(next);
      series.add(next.series);
      next.reasons.forEach(key => covered.add(key));
    }
    return shortlist;
  }
  const core = { rank, steps, sanitize, size, priorityOptions };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  else root.DalstrongKnifeFinderV2Core = core;
})(typeof window !== 'undefined' ? window : globalThis);
